import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Tooltip, Drawer, Typography, Flex, Grid } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { App } from "antd";
import { fetchLocationsList, deleteLocation } from "../locationsThunks";
import { setSelectedLocation, setIsEditLocation } from "../locationsSlice";
import { selectLocationsList } from "../locationsSelectors";
import { getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";

const { Title } = Typography;

const LocationList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const locationsList = useSelector(selectLocationsList);
  const authInfo = useSelector((state) => state.auth?.auth_info);
  let [access_type, access_type_set] = useState("");
  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
  const [helpOpen, setHelpOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  useEffect(() => {
    const access = getAccessType("location_entry");
    access_type_set(access);
  }, []);

  const handleDelete = async (location_id, location_name) => {
    try {
      await dispatch(deleteLocation(location_id)).unwrap();
      modal.success({
        title: `Location "${location_name}" deleted successfully`,
      });
      dispatch(fetchLocationsList());
      dispatch(setSelectedLocation(null));
      dispatch(setIsEditLocation(false));
    } catch (error) {
      modal.warning({
        title: error.message || "Delete failed",
      });
    }
  };

  const columns = [
    {
      title: "NO.",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Location Name",
      dataIndex: "location_name",
      sorter: (a, b) => a.location_name?.localeCompare(b.location_name),
    },
    ...(access_type === "WRITE"
      ? [
        {
          title: "Actions",
          render: (_, record) => (
            <>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => {
                  dispatch(setSelectedLocation(record));
                  dispatch(setIsEditLocation(true));
                  navigate("addNewLocation");
                }}
                disabled={!(isAdminUser || canEdit)}
              />
              <Button
                type="link"
                danger
                disabled={!(isAdminUser || canDelete)}
                icon={<DeleteOutlined />}
                onClick={() => {
                  modal.confirm({
                    title: "Are you sure?",
                    content: `This will delete location: ${record.location_name}`,
                    okText: "Yes, Delete",
                    cancelText: "Cancel",
                    onOk: () =>
                      handleDelete(record.location_id, record.location_name),
                  });
                }}
              />
            </>
          ),
        },
      ]
      : []),
  ];

  useEffect(() => {
    dispatch(setSelectedLocation(null));
    dispatch(setIsEditLocation(false));
    dispatch(fetchLocationsList());
  }, [dispatch]);

  return (
    <>
      <Row justify="end" style={{ marginBottom: "32px", marginTop: "32px" }}>
        <Col>
        <Tooltip title="Help">
        <Button
          type="text"
          icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
          onClick={() => setHelpOpen(true)}
        />
      </Tooltip>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                className="traic-button"
                icon={<PlusOutlined />}
                style={{
                  minWidth: "112px",
                  height: "36px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
                onClick={() => {
                  dispatch(setSelectedLocation(null));
                  dispatch(setIsEditLocation(false));
                  navigate("addNewLocation");
                }}
                data-testid="btn-add-new-location"
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>
      <Drawer
        title="Location Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "12px",
            }}
          >
            Location Management
          </h2>

          <p style={{ color: "#555", marginBottom: "16px", marginLeft: 17 }}>
            This section helps manage business locations, operational areas,
            delivery regions, and location-based workflows used throughout the ERP system.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>What You Can Do</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>Create and manage location records</li>
              <li>Edit location names and operational details</li>
              <li>Delete locations if permitted</li>
              <li>Track areas used in customer and supplier management</li>
              <li>Manage location-based inventory and branch workflows</li>
              <li>Organize delivery and operational regions</li>
              <li>Maintain structured business area records</li>
              <li>View all available locations in a searchable table</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Locations are used across multiple ERP modules.</li>
              <li>Deleting locations may affect linked records and transactions.</li>
              <li>Location data helps organize operational workflows.</li>
              <li>Only authorized users can edit or delete locations.</li>
              <li>Maintain accurate location names for reporting consistency.</li>
            </ul>
          </div>
        </div>
      </Drawer>
      <Table
        columns={columns}
        dataSource={locationsList}
        rowKey="location_id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />
    </>
  );
};

export default LocationList;
